import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Trophy, Zap, Coins } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";

const TRIGGERS = [
  { v: "task_completed", label: "When I complete a task" },
  { v: "focus_completed", label: "When a focus session ends" },
  { v: "habit_checkin", label: "When I check in a habit" },
  { v: "streak_reached", label: "When I hit a streak" },
];

export default function Rewards() {
  const [sum, setSum] = useState(null);
  const [rules, setRules] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", trigger: "task_completed", amount: 20 });

  const load = async () => {
    setSum((await api.get("/rewards/summary")).data);
    setRules((await api.get("/rules")).data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) return;
    await api.post("/rules", { name: form.name, trigger: form.trigger, action: { type: "award_xp", amount: Number(form.amount) } });
    setOpen(false); setForm({ name: "", trigger: "task_completed", amount: 20 });
    load();
  };
  const del = async (r) => { await api.delete(`/rules/${r.rule_id}`); load(); };

  return (
    <div className="min-h-screen px-10 pt-10 pb-16 relative">
      <div className="orb orb-purple" style={{ width: 500, height: 500, top: -150, left: -150, opacity: 0.3 }} />
      <div className="flex items-baseline justify-between mb-6 relative">
        <h1 className="serif-italic text-5xl text-white/85">Rewards</h1>
        <button className="pill" onClick={() => setOpen(true)} data-testid="new-rule"><Plus size={13} /> New rule</button>
      </div>
      <div className="h-px bg-white/10 mb-10" />

      <div className="grid md:grid-cols-3 gap-4 mb-10">
        <div className="card-base p-6" data-testid="stat-level">
          <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-widest"><Trophy size={13} /> Level</div>
          <div className="serif-italic text-6xl mt-2" style={{ color: "var(--orange)" }}>{sum?.level ?? 1}</div>
          <div className="mt-4 h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full" style={{ width: `${sum?.progress_pct || 0}%`, background: "var(--orange)" }} />
          </div>
          <div className="text-xs text-white/40 mt-2">{sum?.xp_to_next_level ?? 0} XP to next level</div>
        </div>
        <div className="card-base p-6" data-testid="stat-xp">
          <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-widest"><Zap size={13} /> Total XP</div>
          <div className="serif-italic text-6xl mt-2">{sum?.xp ?? 0}</div>
        </div>
        <div className="card-base p-6" data-testid="stat-coins">
          <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-widest"><Coins size={13} /> Coins</div>
          <div className="serif-italic text-6xl mt-2" style={{ color: "#C77DFF" }}>{sum?.coins ?? 0}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <div className="text-xs uppercase tracking-widest text-white/40 mb-3">Recent events</div>
          <div className="card-base divide-y divide-white/5">
            {(sum?.recent || []).length === 0 && <div className="p-6 text-white/40 text-sm">Complete tasks and focus sessions to earn XP.</div>}
            {(sum?.recent || []).map(e => (
              <div key={e.event_id} className="p-4 flex justify-between items-center text-sm">
                <div>
                  <div className="text-white/85">{e.reason}</div>
                  <div className="text-xs text-white/40">{format(parseISO(e.created_at), "MMM d, HH:mm")}</div>
                </div>
                <div className="serif-italic text-xl" style={{ color: "var(--orange)" }}>+{e.amount}</div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-white/40 mb-3">Your rules</div>
          <div className="space-y-3">
            {rules.length === 0 && <div className="text-white/40 text-sm">No custom rules yet. Create one to shape your Productivity Economy.</div>}
            {rules.map(r => (
              <div key={r.rule_id} className="card-base p-4 flex justify-between items-center" data-testid={`rule-${r.rule_id}`}>
                <div>
                  <div className="text-white/85">{r.name}</div>
                  <div className="text-xs text-white/40 mt-0.5">{TRIGGERS.find(t => t.v === r.trigger)?.label} → +{r.action?.amount || 0} XP</div>
                </div>
                <button onClick={() => del(r)} className="text-white/30 hover:text-white/70"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[color:var(--surface)] border-white/10">
          <DialogHeader><DialogTitle className="serif-italic text-2xl">New rule</DialogTitle></DialogHeader>
          <Input placeholder="Rule name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/10" data-testid="rule-name" />
          <Select value={form.trigger} onValueChange={v => setForm({ ...form, trigger: v })}>
            <SelectTrigger className="bg-white/5 border-white/10" data-testid="rule-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>{TRIGGERS.map(t => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" min="1" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="XP amount" className="bg-white/5 border-white/10" data-testid="rule-amount" />
          <div className="flex justify-end"><Button onClick={create} style={{ background: "var(--orange)" }} data-testid="rule-save">Save</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
